import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router";
import { SwiperSlide } from "swiper/react";

import ExpandableSection from "@components/animations/ExpandableSection";
import styles from "@components/Article.module.css";
import Button from "@components/buttons/Button";
import SecondaryButton from "@components/buttons/SecondaryButton";
import TertiaryButton from "@components/buttons/TertiaryButton";
import WebinarRegistrationButton from "@components/buttons/WebinarRegistrationButton";
import ArticleCard from "@components/cards/ArticleCard";
import MyStoryLeadForm from "@components/forms/MyStoryLeadForm";
import CardSlides from "@components/layout/CardSlides";
import Modal from "@components/Modal";
import Icon from "@components/text/Icon";
import BodyCopy from "@components/typography/BodyCopy";
import ExternalLink from "@components/typography/ExternalLink";
import Heading1 from "@components/typography/Heading1";
import Heading2 from "@components/typography/Heading2";
import Video from "@components/Video";
import useSubscribeToJourney from "@hooks/subscriptions/useSubscribeToJourney";
import useMe from "@hooks/useMe";
import useMyClient from "@hooks/useMyClient";
import useOpener from "@hooks/useOpener";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import { automaticallyRegisterForArticleIdKey } from "@typing/Constants";
import {
  ArticleContentTypeEnum,
  ArticleFragment as ArticleType,
  GetJourneyWithExpertDocument,
  GetJourneyWithExpertQuery,
  GetJourneyWithExpertQueryVariables
} from "@typing/Generated";
import { articleHasVideo } from "@utils/articleUtils";
import { imageUrl } from "@utils/imageUtils";

type Props = {
  article: ArticleType;
  journeyId: string | null;
  refetch: () => void;
  videoPlayedAppEventCreate?: () => void;
};

const Article = ({ article, journeyId, refetch, videoPlayedAppEventCreate }: Props) => {
  const intl = useIntl();
  const intervalRef = useRef<NodeJS.Timeout | undefined>();
  const myClient = useMyClient();
  const myUser = useMe();
  const history = useHistory();
  const transcriptOpener = useOpener();
  const myStoryLeadOpener = useOpener();
  const myStoryLeadSuccessOpener = useOpener();
  const [played, setPlayed] = useState(false);

  const [getJourney, getJourneyQuery] = useLazyQuery<GetJourneyWithExpertQuery, GetJourneyWithExpertQueryVariables>(
    GetJourneyWithExpertDocument
  );

  useSubscribeToJourney(journeyId, () => {
    getJourneyQuery.refetch();
  });

  useEffect(() => {
    if (journeyId) {
      getJourney({
        variables: { id: journeyId }
      });
    }
  }, [getJourney, journeyId]);

  const onVideoPlay = useCallback(() => {
    if (!played && videoPlayedAppEventCreate) {
      videoPlayedAppEventCreate();
      setPlayed(true);
    }
  }, [played, videoPlayedAppEventCreate]);

  useEffect(() => {
    if (article.photoScanning && !intervalRef.current) {
      intervalRef.current = setInterval(() => {
        refetch();
      }, 5000);
    } else if (!article.photoScanning && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };
  }, [article, refetch]);

  const handleMyStoryLeadSuccess = () => {
    myStoryLeadOpener.close();
    myStoryLeadSuccessOpener.open();
  };

  const handleActivateToRegister = () => {
    sessionStorage.setItem(automaticallyRegisterForArticleIdKey, article.id);
    history.push("/sign-up");
  };

  const sourcesOpener = useOpener();

  const sanitizedText = useSanitizeHtml(article.text, journeyId, article);
  const sanitizedTranscript = useSanitizeHtml(article.transcript, journeyId, article);

  useEffect(() => {
    if (sessionStorage.getItem(automaticallyRegisterForArticleIdKey) === article.id) {
      sessionStorage.removeItem(automaticallyRegisterForArticleIdKey);
    }
  }, [article.id]);

  return (
    <>
      <div className={styles.articleGrid}>
        <div>
          <div className={styles.title}>
            <Heading1>{article.title}</Heading1>
          </div>

          <div className={styles.imgContainer}>
            {articleHasVideo(article) && article.videoUrl ? (
              <div className={styles.video}>
                <Video onPlay={onVideoPlay} posterUrl={article.photoUrl} videoUrl={article.videoUrl} />
              </div>
            ) : article.photoUrl ? (
              <img
                alt={intl.formatMessage({ id: "accessibility.altTags.article" })}
                className={styles.image}
                src={article.photoUrl}
              />
            ) : null}
          </div>

          {/* If it is an incomplete webinar then we want to let you register for it if you are a client. If you
              are not logged in, we want to show you a thing that encourages you to register in order to attend.
              If you are logged in as a non-client, you get nuttin. */}
          {article.contentType === ArticleContentTypeEnum.WEBINAR && !article.webinarCompleted && (
            <>
              {myClient ? (
                <WebinarRegistrationButton article={article} journeyId={journeyId} />
              ) : (
                <>
                  {!myUser && (
                    <Button onClick={handleActivateToRegister}>
                      <FormattedMessage id="pages.explore.events.activateToRegister" />
                    </Button>
                  )}
                </>
              )}
            </>
          )}

          {[ArticleContentTypeEnum.TEXT, ArticleContentTypeEnum.WEBINAR].includes(article.contentType) && (
            <div>{sanitizedText}</div>
          )}
          {articleHasVideo(article) && (
            <>
              <TertiaryButton
                icon={transcriptOpener.isOpen ? "chevronUp" : "chevronDown"}
                iconPosition="end"
                onClick={transcriptOpener.toggle}
                textKey={transcriptOpener.isOpen ? "dictionary.hideTranscript" : "dictionary.viewTranscript"}
              />
              <div className={transcriptOpener.isOpen ? "" : "ion-hide"}>{sanitizedTranscript}</div>
            </>
          )}
        </div>
        <div>
          <div className={styles.quote}>
            <Heading2 bold={false} color="gray-500">
              {article.quote}
            </Heading2>
          </div>
          {article.sources.length > 0 && (
            <div>
              <div className={styles.sourcesOpener} onClick={sourcesOpener.toggle}>
                <div className={styles.sourcesAndIcon}>
                  <Icon icon="book" />
                  <BodyCopy bold color="primary">
                    <FormattedMessage id="models.article.sources" />
                  </BodyCopy>
                </div>
                <Icon icon={sourcesOpener.isOpen ? "chevronUp" : "chevronDown"} />
              </div>
              <div>
                <ExpandableSection isOpen={sourcesOpener.isOpen} key="article-sources">
                  <div className={styles.articleSources}>
                    {article.sources.map(source => (
                      <div key={source.value}>
                        <ExternalLink href={source.value}>{source.label}</ExternalLink>
                      </div>
                    ))}
                  </div>
                </ExpandableSection>
              </div>
            </div>
          )}
        </div>
      </div>

      {journeyId && article.relatedArticles.length > 0 && (
        <div className={styles.relatedContent}>
          <div className={styles.relatedContentHeading}>
            <Heading2 color="primary">
              <FormattedMessage id="pages.article.relatedContent" />
            </Heading2>
          </div>

          <CardSlides itemCount={article.relatedArticles.length}>
            {article.relatedArticles.map(article => (
              <SwiperSlide key={article.id}>
                <ArticleCard article={article} journeyId={journeyId} webinarSmall />
              </SwiperSlide>
            ))}
          </CardSlides>
        </div>
      )}

      {article.contentType === ArticleContentTypeEnum.MY_STORY && journeyId && (
        <>
          <Modal
            opener={myStoryLeadOpener}
            title={intl.formatMessage({
              id: "pages.community.shareYourStory"
            })}
          >
            {({ modalDisplayed }) => (
              <>
                <div className={styles.helpOthers}>
                  <FormattedMessage id="pages.community.helpOthers" />
                </div>
                <MyStoryLeadForm
                  autofocus={modalDisplayed}
                  journeyId={journeyId}
                  onCancel={myStoryLeadOpener.close}
                  onSuccess={handleMyStoryLeadSuccess}
                />
              </>
            )}
          </Modal>
          <Modal opener={myStoryLeadSuccessOpener}>
            {() => (
              <div className={styles.center}>
                <img
                  alt={intl.formatMessage({ id: "accessibility.altTags.shareStory" })}
                  className={styles.storyImg}
                  src={imageUrl("communityShareStory")}
                />
                <div className={styles.hugeText}>
                  <FormattedMessage id="pages.community.thanksForSharing" />
                </div>
                <div className={styles.smallText}>
                  <FormattedMessage id="pages.community.someoneFromGrayce" />
                </div>
                <div className={styles.close}>
                  <SecondaryButton expand="block" onClick={myStoryLeadSuccessOpener.close} textKey="dictionary.close" />
                </div>
              </div>
            )}
          </Modal>
        </>
      )}
    </>
  );
};

export default Article;
